import React, { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigationType } from 'react-router-dom';
import { trackPageView } from './utils/analytics';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { CartProvider } from './contexts/CartContext';
import { PopupProvider } from './contexts/PopupContext';
import { FavoritesProvider } from './contexts/FavoritesContext';
import { CategoryProvider } from './contexts/CategoryContext';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import MiniCart from './components/MiniCart';
import PrivateRoute from './components/PrivateRoute';

// Eagerly loaded: the homepage is where most visits start, so it ships in the
// main bundle and never waits on another chunk.
import Home from './pages/Home';

// Lazily loaded: the other customer pages. Products, product detail and cart
// are prefetched once the first page has loaded (see App), so moving into the
// shop doesn't wait on a download. Checkout is left out of that on purpose:
// importing @stripe/stripe-js starts downloading Stripe.js (~250 KB).
const loadProducts = () => import('./pages/Products');
const loadProductDetail = () => import('./pages/ProductDetail');
const loadCart = () => import('./pages/Cart');
const Products = lazy(loadProducts);
const ProductDetail = lazy(loadProductDetail);
const Cart = lazy(loadCart);
const Login = lazy(() => import('./pages/Login'));
const Signup = lazy(() => import('./pages/Signup'));
const Checkout = lazy(() => import('./pages/Checkout'));
const OrderConfirmation = lazy(() => import('./pages/OrderConfirmation'));
const Orders = lazy(() => import('./pages/Orders'));
const OrderDetail = lazy(() => import('./pages/OrderDetail'));
const Profile = lazy(() => import('./pages/Profile'));
const Favorites = lazy(() => import('./pages/Favorites'));

// Lazily loaded: admin pages — only ever requested by admins, no point
// shipping them in the main bundle to every customer.
const AdminRoute = lazy(() => import('./components/AdminRoute'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const AdminProducts = lazy(() => import('./pages/AdminProducts'));
const AdminProductForm = lazy(() => import('./pages/AdminProductForm'));
const AdminCategories = lazy(() => import('./pages/AdminCategories'));
const AdminComponents = lazy(() => import('./pages/AdminComponents'));
const AdminHomepage = lazy(() => import('./pages/AdminHomepage'));
const AdminNavbar = lazy(() => import('./pages/AdminNavbar'));
const AdminOrders = lazy(() => import('./pages/AdminOrders'));
const AdminInventory = lazy(() => import('./pages/AdminInventory'));
const AdminFooterPages = lazy(() => import('./pages/AdminFooterPages'));
const AdminEmailMarketing = lazy(() => import('./pages/AdminEmailMarketing'));
const AdminShipping = lazy(() => import('./pages/AdminShipping'));

// Lazily loaded: policy/info pages — rare visits, plain content.
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const RefundPolicy = lazy(() => import('./pages/RefundPolicy'));
const ShippingPolicy = lazy(() => import('./pages/ShippingPolicy'));
const Contact = lazy(() => import('./pages/Contact'));
const MarketingTerms = lazy(() => import('./pages/MarketingTerms'));

function StorefrontChrome({ children }) {
  const { pathname } = useLocation();
  return pathname === '/admin' || pathname.startsWith('/admin/') ? null : children;
}

function RouteFallback() {
  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="w-6 h-6 border-2 border-gray-300 border-t-gray-700 rounded-full animate-spin" />
    </div>
  );
}

// Checkout reads the signed-in user once when it mounts, so hold it until
// Firebase has restored the session.
function AuthReady({ children }) {
  const { loading } = useAuth();
  return loading ? <RouteFallback /> : children;
}

/**
 * Scroll behaviour for SPA navigation.
 *
 * - PUSH/REPLACE (forward navigation, link click): scroll to top.
 * - POP (back/forward button): restore the scroll position the user was at
 *   when they left this entry, so returning from a product detail lands
 *   them on the same spot in the listing.
 *
 * Positions are keyed by `location.key` (unique per history entry) and
 * persisted in sessionStorage so a refresh on the listing keeps state.
 * Restoration retries with rAF until the document is tall enough to
 * accommodate the saved offset, since pages typically fetch data on mount.
 */
function ScrollManager() {
  const location = useLocation();
  const navigationType = useNavigationType();

  // Take ownership of scroll restoration from the browser.
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      const previous = window.history.scrollRestoration;
      window.history.scrollRestoration = 'manual';
      return () => {
        window.history.scrollRestoration = previous;
      };
    }
  }, []);

  // Track scroll for the currently mounted location and persist it.
  useEffect(() => {
    const key = `scroll:${location.key}`;
    let timeout;
    const save = () => sessionStorage.setItem(key, String(window.scrollY));
    const debouncedSave = () => {
      clearTimeout(timeout);
      timeout = setTimeout(save, 100);
    };
    window.addEventListener('scroll', debouncedSave, { passive: true });
    return () => {
      clearTimeout(timeout);
      save(); // capture final position before navigating away
      window.removeEventListener('scroll', debouncedSave);
    };
  }, [location.key]);

  // Decide where to scroll on each navigation.
  useEffect(() => {
    if (navigationType === 'POP') {
      const saved = sessionStorage.getItem(`scroll:${location.key}`);
      if (saved !== null) {
        const target = parseInt(saved, 10);
        let attempts = 0;
        const tryRestore = () => {
          attempts += 1;
          const docHeight = document.documentElement.scrollHeight;
          if (docHeight >= target + window.innerHeight || attempts > 30) {
            window.scrollTo(0, target);
          } else {
            requestAnimationFrame(tryRestore);
          }
        };
        requestAnimationFrame(tryRestore);
        return;
      }
    }
    window.scrollTo(0, 0);
  }, [location.key, navigationType]);

  // Fire a GA4 page_view on every navigation, including the initial mount.
  // Defer to the next tick so the new page has a chance to set document.title.
  useEffect(() => {
    // Admin searches may contain customer details and are not storefront visits.
    if (location.pathname === '/admin' || location.pathname.startsWith('/admin/')) return undefined;
    const timer = setTimeout(() => {
      trackPageView(location.pathname + location.search);
    }, 0);
    return () => clearTimeout(timer);
  }, [location.pathname, location.search]);

  return null;
}

function App() {
  // Once the first page has finished loading and the browser is idle, fetch
  // the pages shoppers usually open next.
  useEffect(() => {
    const prefetch = () => [loadProducts, loadProductDetail, loadCart].forEach(load => load().catch(() => {}));
    const schedule = () => (window.requestIdleCallback ? window.requestIdleCallback(prefetch) : setTimeout(prefetch, 1000));
    if (document.readyState === 'complete') schedule();
    else window.addEventListener('load', schedule, { once: true });
    return () => window.removeEventListener('load', schedule);
  }, []);

  return (
    <Router>
      <ScrollManager />
      <AuthProvider>
        <CartProvider>
          <FavoritesProvider>
          <CategoryProvider>
          <PopupProvider>
            <div className="App flex flex-col min-h-screen">
            <StorefrontChrome><Navbar /></StorefrontChrome>
            <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/products" element={<Products />} />
              <Route path="/products/:id" element={<ProductDetail />} />
              
              {/* Cart and Checkout allow guest access */}
              <Route path="/cart" element={<Cart />} />
              <Route path="/checkout" element={<AuthReady><Checkout /></AuthReady>} />
              
              <Route path="/order-confirmation" element={<OrderConfirmation />} />
              
              <Route path="/orders" element={
                <PrivateRoute>
                  <Orders />
                </PrivateRoute>
              } />
              
              <Route path="/orders/:id" element={
                <PrivateRoute>
                  <OrderDetail />
                </PrivateRoute>
              } />
              
              <Route path="/profile" element={
                <PrivateRoute>
                  <Profile />
                </PrivateRoute>
              } />

              <Route path="/favorites" element={
                <PrivateRoute>
                  <Favorites />
                </PrivateRoute>
              } />

              {/* Keep the admin shell mounted while staff switch between pages. */}
              <Route path="/admin" element={<AdminRoute />}>
                <Route index element={<AdminDashboard />} />
                <Route path="products" element={<AdminProducts />} />
                <Route path="products/new" element={<AdminProductForm />} />
                <Route path="products/edit/:id" element={<AdminProductForm />} />
                <Route path="categories" element={<AdminCategories />} />
                <Route path="components" element={<AdminComponents />} />
                <Route path="homepage" element={<AdminHomepage />} />
                <Route path="orders" element={<AdminOrders />} />
                <Route path="inventory" element={<AdminInventory />} />
                <Route path="navbar" element={<AdminNavbar />} />
                <Route path="footer-pages" element={<AdminFooterPages />} />
                <Route path="email-marketing" element={<AdminEmailMarketing />} />
                <Route path="shipping" element={<AdminShipping />} />
              </Route>

              {/* Policy & Info Pages */}
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms-of-service" element={<TermsOfService />} />
              <Route path="/refund-policy" element={<RefundPolicy />} />
              <Route path="/shipping-policy" element={<ShippingPolicy />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/marketing-terms" element={<MarketingTerms />} />
            </Routes>
            </Suspense>
            <StorefrontChrome><MiniCart /><Footer /></StorefrontChrome>
            </div>
          </PopupProvider>
          </CategoryProvider>
          </FavoritesProvider>
        </CartProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
